// src/SectionResultsViewer.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchTextWithDiagnostics } from "./utils/helpers";

// ------------------------
// helpers
// ------------------------
function safeArr(x) {
  return Array.isArray(x) ? x : [];
}

function normalizeChildList(attempt) {
  return attempt?.lstChilds || attempt?.lstChild || attempt?.children || attempt?.childs || [];
}

function pickLatestAttempt(attempts) {
  const arr = safeArr(attempts);
  if (arr.length === 0) return null;

  return arr.reduce((best, cur) => {
    const b = Number(best?.exec_id ?? best?.execId ?? -1);
    const c = Number(cur?.exec_id ?? cur?.execId ?? -1);
    return c > b ? cur : best;
  }, arr[0]);
}

function computeAttemptStats(attempt) {
  const childs = safeArr(normalizeChildList(attempt));
  let right = 0;
  let wrong = 0;
  let un = 0;

  for (const c of childs) {
    const rw = String(c?.right_or_wrong ?? "").toLowerCase();
    // Handle various formats: "right"/"wrong", "1"/"0", "yes"/"no"
    if (rw === "right" || rw === "1" || rw === "yes") right++;
    else if (rw === "wrong" || rw === "0" || rw === "no") wrong++;
    else un++;
  }
  const total = right + wrong + un;
  const accuracy = total ? Math.round((right / total) * 100) : 0;

  return { total, right, wrong, un, accuracy };
}

// Now imported from utils/helpers

async function mapInBatches(items, batchSize, mapper) {
  const out = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    // eslint-disable-next-line no-await-in-loop
    const res = await Promise.all(batch.map(mapper));
    out.push(...res);
  }
  return out;
}

function StatCard({ label, value, hint, isDarkMode }) {
  return (
    <div className={`rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'} p-4 shadow-sm`}>
      <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</div>
      {hint ? <div className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{hint}</div> : null}
    </div>
  );
}

function SecondaryButton({ children, className = "", isDarkMode, ...props }) {
  return (
    <button
      className={`inline-flex items-center justify-center px-4 py-2 rounded-xl border ${isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'} disabled:opacity-50 disabled:cursor-not-allowed transition ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`inline-flex items-center justify-center px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

function StatusPill({ accuracy }) {
  if (accuracy >= 70) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
        ✅ Pass <span className="font-normal text-emerald-700/80">{accuracy}%</span>
      </span>
    );
  }
  if (accuracy > 0) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
        ❌ Fail <span className="font-normal text-red-700/80">{accuracy}%</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
      ⏳ Unanswered
    </span>
  );
}

export default function SectionResultsViewer({
  token,
  appName,
  section,
  categories = [],
  apiBase,
  execAppender,
  onBack,
  onUnauthorized,
  onOpenCategoryResults,
  isDarkMode,
}) {
  const [questions, setQuestions] = useState([]); // {exam_id, question, category, right_or_wrong, user_choice, correct_answer, explanation, ...}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedExplanation, setExpandedExplanation] = useState(null);

  const load = useCallback(async () => {
    if (!token || !appName || !section) return;
    setLoading(true);
    setError("");

    const base = (apiBase || "").replace(/\/$/, "");
    const allQuestions = [];

    try {
      const results = await mapInBatches(categories, 3, async (category) => {
        // Load ALL questions for this category (with pagination)
        const allCategoryQuestions = [];
        let currentExamId = 0;
        let hasMore = true;

        while (hasMore) {
          const questionsUrl =
            `${base}/exams${execAppender || ""}/${encodeURIComponent(appName)}` +
            `/${encodeURIComponent(section)}/${encodeURIComponent(category)}` +
            `?CurrentExamID=${currentExamId}&prevNext=Next`.replace(/\+/g, "%2B");

          console.log(`📤 Fetching page for ${category}: CurrentExamID=${currentExamId}`);
          const qRes = await fetchTextWithDiagnostics(questionsUrl, {
            headers: { Authorization: token },
          }, { timeoutMs: 20000 });

          if (!qRes.ok || !qRes.text) {
            hasMore = false;
            break;
          }

          try {
            const qArr = JSON.parse(qRes.text);
            const questions = safeArr(qArr);
            
            if (questions.length === 0) {
              hasMore = false;
              break;
            }

            // Store all questions with their data
            for (const q of questions) {
              const examId = q?.exam_id ?? q?.examId ?? q?.id ?? 0;
              if (examId) {
                allCategoryQuestions.push({
                  exam_id: examId,
                  question: q?.exam_name ?? q?.question ?? "Question",
                  category,
                  right_or_wrong: "Unanswered",
                  user_choice: "—",
                  correct_answer: "—",
                  explanation: q?.explanation || q?.explaination || "",
                  questionData: q,
                });
                currentExamId = Math.max(currentExamId, Number(examId));
              }
            }

            console.log(`✅ Fetched ${questions.length} questions for ${category}, next ID: ${currentExamId}`);
          } catch (e) {
            console.error(`❌ Parse error for ${category}:`, e);
            hasMore = false;
            break;
          }
        }

        console.log(`📊 Total questions loaded for ${category}: ${allCategoryQuestions.length}`);

        let attemptedQuestionIds = new Set();

        // Now load the attempts to overlay attempt data on top
        const attemptsUrl =
          `${base}/exams${execAppender || ""}/${encodeURIComponent(appName)}` +
          `/users/search_exam_exec${execAppender || ""}` +
          `?category_name=${encodeURIComponent(category)}` +
          `&section_name=${encodeURIComponent(section)}` +
          `&is_completed=N`;

        const res = await fetchTextWithDiagnostics(attemptsUrl, {
          headers: { Authorization: token },
        });

        if (res.status === 401) {
          onUnauthorized?.();
          return allCategoryQuestions;
        }

        if (!res.ok) {
          return allCategoryQuestions;
        }

        let attempts = [];
        try {
          attempts = JSON.parse(res.text);
        } catch {
          attempts = [];
        }

        const safeAttempts = safeArr(attempts);
        const catQuestions = [];

        // Process attempts and update questions
        for (const attempt of safeAttempts) {
          const childs = safeArr(normalizeChildList(attempt));
          for (const child of childs) {
            const examId = child?.exam_id ?? child?.examId ?? child?.id;
            attemptedQuestionIds.add(examId);

            // Extract user's selected choice with full text
            let userChoice = "—";
            for (let i = 1; i <= 4; i++) {
              const chosen = child[`answer_choosen${i}`] || child[`answerChoosen${i}`];
              if (chosen === "Y" || chosen === true) {
                // Find the full question data to get answer text
                const qData = allCategoryQuestions.find(q => q.exam_id == examId)?.questionData || {};
                userChoice = qData[`answer${i}`] || qData[`Answer${i}`] || `Option ${String.fromCharCode(64 + i)}`;
                break;
              }
            }

            // Extract correct answer
            let correctAnswer = "—";
            for (let i = 1; i <= 4; i++) {
              const isCorrect = child[`isAnswerCorrect${i}`] || child[`IsAnswerCorrect${i}`];
              if (isCorrect === "Y" || isCorrect === true) {
                // Find the full question data to get answer text
                const qData = allCategoryQuestions.find(q => q.exam_id == examId)?.questionData || {};
                correctAnswer = qData[`answer${i}`] || qData[`Answer${i}`] || `Option ${String.fromCharCode(64 + i)}`;
                break;
              }
            }

            // Extract explanation
            const explanation =
              child?.explanation ||
              child?.explaination ||
              child?.answerExplanation ||
              "";

            catQuestions.push({
              exam_id: examId ?? "—",
              question: child?.exam_name ?? child?.question ?? "Question",
              category,
              right_or_wrong: String(child?.right_or_wrong || "Unanswered").trim(),
              user_choice: userChoice,
              correct_answer: correctAnswer,
              explanation,
            });
          }
        }

        // Combine: attempted questions + unattempted questions
        const unattemptedQuestions = allCategoryQuestions.filter(q => !attemptedQuestionIds.has(q.exam_id));
        return [...catQuestions, ...unattemptedQuestions];
      });

      // Flatten results
      for (const categoryQuestions of results) {
        allQuestions.push(...categoryQuestions);
      }

      setQuestions(allQuestions);
    } catch (e) {
      setError(e?.message || "Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, [token, appName, section, categories, apiBase, execAppender, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    let right = 0;
    let wrong = 0;
    let un = 0;

    for (const q of questions) {
      const rw = String(q?.right_or_wrong || "").toLowerCase().trim();
      // Handle various formats: "right"/"wrong", "1"/"0", "yes"/"no", empty/unanswered
      if (rw === "right" || rw === "1" || rw === "yes") {
        right++;
      } else if (rw === "wrong" || rw === "0" || rw === "no") {
        wrong++;
      } else if (rw === "" || rw === "unanswered") {
        // Empty or explicitly "Unanswered"
        un++;
      } else {
        // Any other value defaults to unanswered
        un++;
      }
    }

    const total = right + wrong + un;
    const accuracy = total > 0 ? Math.round((right / total) * 100) : 0;
    return { total, right, wrong, un, accuracy };
  }, [questions]);

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <button onClick={onBack} className={`text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'} hover:underline`} type="button" title="Return to section selection">
          ← Back
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          <SecondaryButton onClick={load} disabled={loading} isDarkMode={isDarkMode} title="Reload all attempt results for this section">
            {loading ? "Refreshing…" : "Refresh"}
          </SecondaryButton>
        </div>
      </div>

      <div className={`rounded-3xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'} shadow-sm`}>
        <div className={`px-6 py-5 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
          <div className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Section Results</div>
          <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mt-1`}>
            {section} • {questions.length} questions
          </div>

          {error ? (
            <div className={`mt-4 rounded-2xl border ${isDarkMode ? 'border-red-800 bg-red-900/30' : 'border-red-200 bg-red-50'} p-4 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
              {error}
            </div>
          ) : null}
        </div>

        <div className="px-6 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-tour="section-results-summary">
            <StatCard label="Total" value={totals.total} isDarkMode={isDarkMode} />
            <StatCard label="Right" value={totals.right} isDarkMode={isDarkMode} />
            <StatCard label="Wrong" value={totals.wrong} isDarkMode={isDarkMode} />
            <StatCard label="Unanswered" value={totals.un} isDarkMode={isDarkMode} />
            <StatCard label="Accuracy" value={`${totals.accuracy}%`} hint="Across all questions" isDarkMode={isDarkMode} />
          </div>

          <div
            className={`rounded-2xl border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'} overflow-hidden`}
            data-tour="section-results-table"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full w-full">
                <thead className={`${isDarkMode ? 'bg-slate-900 border-b border-slate-700' : 'bg-slate-50 border-b border-slate-200'}`}>
                  <tr className="text-left">
                    <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide min-w-48`}>
                      Question
                    </th>
                    <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide min-w-32`}>
                      Category
                    </th>
                    <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide min-w-40`}>
                      Your Choice
                    </th>
                    <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide min-w-40`}>
                      Correct Answer
                    </th>
                    <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide min-w-28`}>
                      Status
                    </th>
                    <th className={`px-4 py-3 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide min-w-24`}>
                      Explanation
                    </th>
                  </tr>
                </thead>

                <tbody className={`${isDarkMode ? 'divide-y divide-slate-700' : 'divide-y divide-slate-100'}`}>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-6 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        Loading…
                      </td>
                    </tr>
                  ) : questions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-6 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        No questions found.
                      </td>
                    </tr>
                  ) : (
                    questions.map((q, idx) => (
                      <React.Fragment key={`${q.category}-${q.exam_id}-${idx}`}>
                        <tr className={`${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}>
                          <td className="px-4 py-4">
                            <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{q.question}</div>
                          </td>

                          <td className="px-4 py-4">
                            <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{q.category}</div>
                          </td>

                        <td className="px-4 py-4">
                          <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-800'} break-words`}>
                            {q.user_choice}
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-800'} break-words`}>
                            {q.correct_answer}
                          </div>
                        </td>

                          <td className="px-4 py-4">
                          {(() => {
                            const rw = String(q.right_or_wrong || "").toLowerCase().trim();
                            if (rw === "right" || rw === "1" || rw === "yes") {
                              return (
                                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                                  ✅ Correct
                                </span>
                              );
                            } else if (rw === "wrong" || rw === "0" || rw === "no") {
                              return (
                                <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
                                  ❌ Incorrect
                                </span>
                              );
                            } else {
                              return (
                                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                                  ⏳ Unanswered
                                </span>
                              );
                            }
                          })()}
                          </td>

                          <td className="px-4 py-4">
                            {q.explanation ? (
                              <button
                                onClick={() =>
                                  setExpandedExplanation(
                                    expandedExplanation === `${q.category}-${q.exam_id}-${idx}` ? null : `${q.category}-${q.exam_id}-${idx}`
                                  )
                                }
                                className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition"
                                title={expandedExplanation === `${q.category}-${q.exam_id}-${idx}` ? "Hide the explanation" : "View the explanation for this question"}
                              >
                                {expandedExplanation === `${q.category}-${q.exam_id}-${idx}` ? "Hide" : "View"}
                              </button>
                            ) : (
                              <span className={`${isDarkMode ? 'text-slate-500' : 'text-slate-400'} text-xs`}>No explanation</span>
                            )}
                          </td>
                        </tr>

                        {expandedExplanation === `${q.category}-${q.exam_id}-${idx}` && q.explanation && (
                          <tr className={`${isDarkMode ? 'bg-slate-800 border-b border-slate-700' : 'bg-slate-50 border-b border-slate-100'}`}>
                            <td colSpan={6} className="px-4 py-4">
                              <div className={`rounded-xl border ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'} p-4`}>
                                <div className={`text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} uppercase tracking-wide mb-2`}>
                                  Explanation
                                </div>
                                <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'} leading-relaxed`}>{q.explanation}</div>
                                <button
                                  onClick={() => setExpandedExplanation(null)}
                                  className={`mt-3 text-xs ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} hover:underline`}
                                  title="Close the explanation"
                                >
                                  Collapse
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={`mt-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Showing all questions from <span className="font-mono">{section}</span> section across {categories.length} categories.
          </div>
        </div>
      </div>
    </div>
  );
}
